import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import './style.scss';
import { IColorwayProductData, TUserSelections } from '../../types/PDP';
import FindInStoreForm from './FindInStoreForm';
import { SiteSettings } from 'src/scripts/utils/environment';
import { hasFISExcludeTag } from 'src/scripts/utils/plp-pdp-shared';
import FindInStoreTile from './FindInStoreTile';
import {
  checkStoreAvailability,
  FIND_IN_STORE_LAT_LONG,
  LatLng,
  StoreAvailabilityParams,
} from '../../../../utils/findInStores';
import { ProductContext } from '../../ProductContext';
import { LoadingMoreSpinner } from 'src/scripts/templates/product-listing-page/components/SmallUIComponents';
import { fireFindInStoreDataLayerEvent } from 'src/scripts/utils/gtm-data-layer';
import { ShippingCountryState, getUsersPreferredCountry } from 'src/scripts/utils/shipping';

const FindInStore = ({
  sku,
  quantityAvailable,
  product,
  userSelections,
  setModalData,
}: {
  sku: string | undefined;
  quantityAvailable: number | undefined;
  product: IColorwayProductData;
  userSelections: TUserSelections;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setModalData: React.Dispatch<React.SetStateAction<any>>;
}) => {
  const findInStoreEnabled = SiteSettings?.enableFindInStore;
  const findInStorePricingDisclaimer = SiteSettings?.findInStorePricingDisclaimer;
  const hideFIS = hasFISExcludeTag(product?.tags);
  const size = userSelections[0].Size;
  const [latLong, setLatLongLocalStorage] = useLocalStorage<string>(FIND_IN_STORE_LAT_LONG, '');
  const [storesData, setStoresData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastValidZip, setLastValidZip] = useState('');
  const { toggleFindInStoreModal, isFindInStoreModalOpen, findInStoreApiError } = useContext(ProductContext);

  // we are receiving country code 'GB' when United Kingdom is selected from currency/shipping dropdown but need to send 'UK' to API
  function mapCountryCode(countryCode: string | null) {
    if (countryCode === null) {
      return null;
    }
    const countryMappings: Record<string, string> = {
      GB: 'UK',
    };
    // check if the provided code is in the mapping, otherwise return the original code
    return countryMappings[countryCode] || countryCode;
  }

  // use this list to maintain FIS supported countries
  const countriesWithAloStores: string[] = ['US', 'CA', 'UK'];
  const preferredCountry = getUsersPreferredCountry();
  const shippingCountryCode = ShippingCountryState.countryCode;
  const userCountry = mapCountryCode(preferredCountry || shippingCountryCode);
  const shouldShowFindInStore =
    userCountry && countriesWithAloStores.includes(userCountry) && findInStoreEnabled && !hideFIS;

  const openModal = () => {
    toggleFindInStoreModal(true);
  };

  const loadStoreAvailability = useCallback(
    async (params: StoreAvailabilityParams) => {
      setIsLoading(true);
      const response = await checkStoreAvailability(params);
      setStoresData(response);
      setModalData({
        product,
        storesData: response,
        size,
        userCountry,
        setLatLongLocalStorage,
        findInStorePricingDisclaimer,
        findInStoreApiError,
        setLastValidZip,
        lastValidZip,
      });
      setIsLoading(false);
      const heapEventPayload = {
        product_title: product.title,
        color: product.colorName,
        size: size,
        sku: sku,
        customer_location: JSON.parse(latLong),
        quantity_available_online: quantityAvailable,
        store_availability: response,
      };

      // switch context for events based on whether or not the modal is open
      const eventName = isFindInStoreModalOpen ? `find_in_store_modal_event` : `find_in_store_pdp_event`;
      fireFindInStoreDataLayerEvent(eventName, sku, quantityAvailable, size, product, JSON.parse(latLong));
      heap.track(eventName, heapEventPayload);
    },
    [
      setModalData,
      product,
      size,
      userCountry,
      setLatLongLocalStorage,
      sku,
      quantityAvailable,
      latLong,
      isFindInStoreModalOpen,
      findInStorePricingDisclaimer,
      findInStoreApiError,
      setLastValidZip,
      lastValidZip,
    ]
  );

  useEffect(() => {
    if (latLong && latLong.length > 0 && sku && userCountry) {
      const coordinates: LatLng = JSON.parse(latLong);
      const params: StoreAvailabilityParams = {
        sku: sku,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        limit: 10,
      };
      loadStoreAvailability(params);
    }
  }, [latLong, loadStoreAvailability, sku, userCountry]);

  if (shouldShowFindInStore) {
    return (
      <div id="FindInStore" className="find-in-store-pdp">
        <p className="find-in-store-pdp-title">Find in Store</p>
        {userCountry ? (
          !size ? (
            <p>Select a Size to Check Store Inventory</p>
          ) : (
            <>
              <FindInStoreForm
                userCountry={userCountry}
                setLatLongLocalStorage={setLatLongLocalStorage}
                findInStoreApiError={findInStoreApiError}
                setLastValidZip={setLastValidZip}
              />
              {latLong && latLong.length > 0 && sku ? (
                isLoading ? (
                  <LoadingMoreSpinner />
                ) : storesData.length > 0 ? (
                  <div className="find-in-store-tile-pdp-container d-flex">
                    {findInStorePricingDisclaimer ? (
                      <p className="find-in-store-pricing-disclaimer black-text">{findInStorePricingDisclaimer}</p>
                    ) : null}
                    {storesData.slice(0, 3).map((storeAvailabilityData: IStoreAvailabilityData) => (
                      <FindInStoreTile
                        store={storeAvailabilityData}
                        openModal={openModal}
                        displayMode={'inline'}
                        key={storeAvailabilityData.location_id}
                      />
                    ))}
                    <button className="details-link" onClick={openModal}>
                      Check Additional Stores
                    </button>
                  </div>
                ) : (
                  <p className="find-in-store-no-results">
                    Sorry, there are no Alo stores found near you. Try a different{' '}
                    {userCountry === 'US' ? 'zip' : 'postal'} code.
                  </p>
                )
              ) : null}
            </>
          )
        ) : (
          <p>Please select a country and currency from the dropdown in the footer.</p>
        )}
      </div>
    );
  } else {
    return null;
  }
};

export default FindInStore;

export interface IStoreAvailabilityData {
  location_id: number;
  available: number;
  available_after_safety_stock: number;
  latitude: number;
  longitude: number;
  hours_of_operation: Array<{
    day_text: string;
    hour_text: string;
  }>;
  name: string;
  address: {
    address1: string;
    address2: string;
    city: string;
    country: string;
    country_code: string;
    phone: string;
    province: string;
    province_code: string;
    postal_code: string;
  };
}
